import * as React from 'react';
import * as styles from "./FullView.css";
import { inject, observer } from 'mobx-react';
import DateTimeService from '../../../services/DateTimeService';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { IALikesAndComments } from '../../likesAndComments/LikesAndComments';
import { MessagesStore } from '../stores';
import * as _html2canvas from "html2canvas";

import NavigatorBar from '../../panels/navigationBar/NavigationBar';
import { Environment } from '../../../services/Environment';
import jsPDF from 'jspdf';
import { IAViewer } from '../../viewer/Viewer';
import { IMessage } from '../../../interfaces/IMessage';


export interface IFullViewProps {
  messagesStore?: MessagesStore;
  getNextPage: () => Promise<void>;
  getPreviousPage: () => void;

}

export interface IFullViewState {
  opacity: number;
  headerImageHasCORS?: boolean;
  printing?: boolean;
}

@inject('messagesStore')
@observer
export class FullView extends React.Component<IFullViewProps, IFullViewState> {

  private readonly dateTimeService: DateTimeService = new DateTimeService();
  private printPanelId: string;

  constructor(props: IFullViewProps) {
    super(props);
    this.state = {
      opacity: 1,
    }
    this.printPanelId = Helper.getRandomStringKey();
  }

  private printMessage(): void {
    this.setState({ printing: true }, () => {
      const html2canvas: any = _html2canvas;

      var panel: HTMLElement;
      let maxWidth = 180;
      panel = document.getElementById(`fullView_${this.printPanelId}`);

      const width = panel.scrollWidth;
      const height = panel.scrollHeight;
      const sizeScale = height / width;

      var printHeight = 297 - 10 - 10;
      const heightScale = height / printHeight;
      var printWidth = width / heightScale;
      var marginLeft = (210 - printWidth) / 2;

      if (printWidth > maxWidth) {
        const difference = printWidth - maxWidth;
        printHeight -= difference * sizeScale;
        printWidth = maxWidth;
        marginLeft += difference / 2;
      }

      html2canvas(panel, {
        allowTaint: false,
        useCORS: true,
        height: height,
        windowHeight: height,
      }).then((canvas: HTMLCanvasElement) => {
        this.setState({ printing: false });
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF();

        pdf.addImage(imgData, 'JPEG', marginLeft, 10, printWidth, printHeight);

        // open pdf in new window
        const newTab = window.open(pdf.output('bloburl').toString(), '_blank');
        newTab.addEventListener('load', () => {
          newTab.print();
        });
      }).catch(() => this.setState({ printing: false }));
    });
  }

  public render(): JSX.Element {
    let item: IMessage;
    let isEventToday: boolean;
    let isOneDayEvent: boolean;
    let headerImage = this.props.messagesStore.messageToOpen != undefined ? this.props.messagesStore.messageToOpen.image : undefined;
    const shouldUpdateLocalCache = this.props.messagesStore.tenantSettings && this.props.messagesStore.tenantSettings.image && this.props.messagesStore.tenantSettings.image.shouldUpdateLocalCache;
    if (headerImage != null) {
      if (shouldUpdateLocalCache) {
        headerImage = headerImage.replace("DefaultRendition", "StandardLarge").replace("version=&", `version=${this.props.messagesStore.messageToOpen.modified ? (new Date(this.props.messagesStore.messageToOpen.modified)).toISOString().replace(/[^\d]/g, '') : ""}&ignoreCache=false&`);
      } else {
        headerImage = headerImage.replace("DefaultRendition", "StandardLarge");
      }
    }

    if (this.props.messagesStore.messages && this.props.messagesStore.messages[0]) {
      const startIndex = this.props.messagesStore.pageNumber - 1;
      const endIndex = startIndex + 1;
      item = this.props.messagesStore.messages.slice(startIndex, endIndex)[0];
      isEventToday = item ? this.dateTimeService.ConvertToDDMMYYYY(item?.startDate) === this.dateTimeService.ConvertToDDMMYYYY(new Date()) : undefined;
      isOneDayEvent = item ? this.dateTimeService.isStartAndEndDateTheSameDay(item?.startDate, item?.endDate, item?.allDayEvent) : undefined;
    }
    let onEdit;
    if (this.props.messagesStore.component === "Event") {
      onEdit = this.props.messagesStore.userRole == "Administrator" || this.props.messagesStore.userRole == "Editor" ? () => { this.props.messagesStore.editMessage(item) } : undefined;
    } else {
      onEdit = this.props.messagesStore.userRole == "Administrator" || this.props.messagesStore.userRole == "Editor"
        && this.props.messagesStore.messageToOpen?.author?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase() ||
        this.props.messagesStore.userRole == "Editor"
        && this.props.messagesStore.messageToOpen?.editor?.email?.toLowerCase() == this.props.messagesStore.user?.email?.toLowerCase()
        ? () => { this.props.messagesStore.editMessage(item) } : undefined
    }
    let videoUrl = item?.videoUrl;
    if ((location.href.includes("sharepoint.com") && item?.videoEmbedCode) || (videoUrl == undefined && item?.videoEmbedCode)) {
      videoUrl = Helper.getVideoSrcFromEmbedCode(item.videoEmbedCode);
    }
    return (
      <div
        id={`fullView_${this.printPanelId}`}
        className={styles.IA_fullView}
        style={{
          backgroundColor: "#ffffff",
          color: "#333333",
          opacity: this.state.opacity,
          transition: "opacity 200ms",
          borderRadius: this.props.messagesStore.borderRadius,
        }}
      >
        {this.props.messagesStore.loading ?
          <div
            className={styles.IA_skeleton}
            style={{
              backgroundColor: "#ffffff"
            }}
          >
            <div className={styles.IA_skeletonImage} />
            <div className={styles.IA_skeletonHeadline} />
            <div className={styles.IA_skeletonHeadline2} />
            <div className={styles.IA_skeletonText} />
            <div className={styles.IA_skeletonText2} />
            <div className={styles.IA_skeletonText3} />
          </div>
          :
          (item != undefined &&
            <div>
              <NavigatorBar
                approvers={this.props.messagesStore.messageToEdit?.approvers}
                highlightColor={this.props.messagesStore.highlightColor}
                backgroundColor={this.props.messagesStore.backgroundColor}
                user={this.props.messagesStore.user}
                itemId={`${item?.id}`}
                tenant={this.props.messagesStore.tenant}
                instance={this.props.messagesStore.instance}
                token={this.props.messagesStore.token}
                environment={this.props.messagesStore.environment}
                source={this.props.messagesStore.source}
                commentsEnabled={this.props.messagesStore.commentsEnabled && !item?.iaConfiguration?.disableComments}
                likesEnabled={this.props.messagesStore.likesEnabled && !item?.iaConfiguration?.disableLikes}
                enableNext={!(this.props.messagesStore.pagingInfo == "" && Math.ceil(this.props.messagesStore.messages.length / this.props.messagesStore.numMessages) === this.props.messagesStore.pageNumber)}
                enablePrevious={this.props.messagesStore.pageNumber != 1}
                enablePrint={this.props.messagesStore.instance && this.props.messagesStore.instance.settings && this.props.messagesStore.instance.settings.enablePrinting}
                likes={item?.likes}
                showEmojiPickerExpandButton={this.props.messagesStore.instance?.settings?.showEmojiPickerExpandButton}
                reactionOptions={this.props.messagesStore.instance?.settings?.reactionOptions}
                commentCount={item?.commentCount}
                showUniqueViews={this.props.messagesStore.showUniqueViews}
                onExportToCalender={`${Environment.getEnvironmentForEventsAPI(this.props.messagesStore.environment)}/${this.props.messagesStore.tenant.replace('.sharepoint.com', '')}/${this.props.messagesStore.instance.instanceName}/event/${item?.id}/export?token=${this.props.messagesStore.token}`}
                uniqueViewsCount={item?.uniqueViews}
                disableSaving={false} // TODO
                isMobile={this.props.messagesStore.isMobile}
                onEdit={onEdit}
                position="relative"
                enableTranslation={this.props.messagesStore.instance?.translationEnabled}
                previousMessage={this.props.messagesStore.showPaging ? () => {
                  this.setState({ opacity: 0 });
                  setTimeout(() => {
                    this.props.messagesStore.clearTranslation();
                    if (this.props.messagesStore.pageNumber != 1) {
                      this.props.getPreviousPage();
                      setTimeout(() => {
                        this.setState({ opacity: 1 });
                      }, 600);
                    }
                  }, 200);
                } : undefined}
                onPrint={() => {
                  Helper.imageHasCORS(headerImage).then((hasCORS) => {
                    this.setState({ headerImageHasCORS: hasCORS }, () => this.printMessage());
                  });
                }}
                nextMessage={this.props.messagesStore.showPaging ? () => {
                  this.setState({ opacity: 0 });
                  setTimeout(() => {
                    this.props.messagesStore.clearTranslation();
                    if (this.props.messagesStore.pagingInfo == "" && Math.ceil(this.props.messagesStore.messages.length / this.props.messagesStore.numMessages) === this.props.messagesStore.pageNumber) {
                    } else {
                      if (this.props.messagesStore.pageNumber * this.props.messagesStore.numMessages < this.props.messagesStore.messages.length) {
                        this.props.messagesStore.pageNumber++;
                      } else {
                        this.props.messagesStore.pageNumber++;
                        this.props.getNextPage().then(() => {
                          this.setState({ opacity: 1 });
                        });
                      }
                    }
                  }, 200);
                } : undefined}
                likesUpdated={(likes: any[]) => {
                  this.props.messagesStore.messages[0].likes = likes;
                }}
                commentsUpdated={(comments: number) => {
                  this.props.messagesStore.messages[0].commentCount = comments;
                }}
                commentsClicked={() => {
                  this.props.messagesStore.updateEditorState();
                  var my_element = document.getElementById(`IALikesAndComments_${this.props.messagesStore.instance.instanceName}_${item?.id}_${this.props.messagesStore.webpartId}`);
                  my_element.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    inline: "nearest"
                  });
                }}
                languages={Helper.getLanguages(new LocalizationService())}
                isTranslating={this.props.messagesStore.isTranslating}
                onTranslate={(language: string) => {
                  this.props.messagesStore.getTranslation(language, item?.title, item?.teaser, item?.content, "");
                }}
              />
              {item?.image &&
                <div
                  className={styles.IA_coverImage}
                  style={item?.image != undefined ? {
                    backgroundImage: shouldUpdateLocalCache ? `url('${item?.image.replace("DefaultRendition", "StandardLarge").replace("version=&", `version=${item?.modified ? (new Date(item?.modified)).toISOString().replace(/[^\d]/g, '') : ""}&ignoreCache=false&`)}')` : `url('${item?.image.replace("DefaultRendition", "StandardLarge")}')`,
                  } : {}}
                >
                  {!isEventToday && !isOneDayEvent && !this.props.messagesStore.hideFullViewCoverOverlay &&
                    <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.messagesStore.borderRadius }}>
                      <div className={styles.IA_startDate}>{`${this.dateTimeService.getDayInMonth(item?.startDate)}. ${this.dateTimeService.getNameOfMonth(item?.startDate, item?.allDayEvent)?.substring(0, 3)?.toUpperCase()}`}</div>
                      <div className={styles.IA_startDateendDateDivider} />
                      <div className={styles.IA_endDate}>{`${this.dateTimeService.getDayInMonth(item?.endDate, item?.allDayEvent)}. ${this.dateTimeService.getNameOfMonth(item?.endDate, item?.allDayEvent)?.substring(0, 3)?.toUpperCase()}`}</div>
                    </div>
                  }
                  {!isEventToday && isOneDayEvent && !this.props.messagesStore.hideFullViewCoverOverlay &&
                    <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.messagesStore.borderRadius }}>
                      <div className={styles.IA_coverOverlayMonth}>{this.dateTimeService.getNameOfMonth(item?.startDate, item?.allDayEvent).substring(0, 3)?.toUpperCase()}</div>
                      <div className={styles.IA_coverOverlayDay}>{this.dateTimeService.getDayInMonth(item?.startDate)}</div>
                    </div>
                  }
                  {isEventToday && isOneDayEvent && !this.props.messagesStore.hideFullViewCoverOverlay &&
                    <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.messagesStore.borderRadius }}>
                      <div className={styles.IA_coverOverlayToday}>{new LocalizationService().strings.DateTimeService_Today?.toUpperCase()}</div>
                    </div>
                  }
                  {isEventToday && !isOneDayEvent && !this.props.messagesStore.hideFullViewCoverOverlay &&
                    <div className={styles.IA_coverOverlay} style={{ borderRadius: this.props.messagesStore.borderRadius }}>
                      <div className={styles.IA_startDate}>{`${this.dateTimeService.getDayInMonth(item?.startDate)}. ${this.dateTimeService.getNameOfMonth(item?.startDate, item?.allDayEvent)?.substring(0, 3)?.toUpperCase()}`}</div>
                      <div className={styles.IA_startDateendDateDivider} />
                      <div className={styles.IA_endDate}>{`${this.dateTimeService.getDayInMonth(item?.endDate, item?.allDayEvent)}. ${this.dateTimeService.getNameOfMonth(item?.endDate, item?.allDayEvent)?.substring(0, 3)?.toUpperCase()}`}</div>
                    </div>
                  }
                </div>
              }
              <div style={{
                padding: 10
              }}>
                <IAViewer
                  hideFullViewDate={this.props.messagesStore.hideFullViewDate}
                  token={this.props.messagesStore.token}
                  tenant={this.props.messagesStore.tenant}
                  environment={this.props.messagesStore.environment}
                  isMobile={this.props.messagesStore.isMobile}
                  highlightColor={this.props.messagesStore.highlightColor}
                  component={this.props.messagesStore.component}
                  instance={this.props.messagesStore.instance}
                  id={this.props.messagesStore.id}
                  isAdmin={this.props.messagesStore.isAdminPage}
                  fontSlots={this.props.messagesStore.fontSlots}
                  imageUrl={undefined}
                  imageCropSettings={item?.iaConfiguration?.cropSettings}
                  onlyShowCoverImageInRollup={item?.iaConfiguration?.onlyShowCoverImageInRollup}
                  videoUrl={videoUrl}
                  title={item?.title}
                  teaser={item?.teaser}
                  composerComponents={item?.iaConfiguration?.composerComponents}
                  categories={item?.categories}
                  startDate={item?.startDate}
                  endDate={item?.endDate}
                  allDayEvent={item?.allDayEvent}
                  location={item?.location}
                  modified={item && item?.modified ? item?.modified : new Date()}
                  author={item?.author}
                  contacts={item?.contacts}
                  editor={item?.editor}
                  attachments={item?.iaConfiguration?.attachments && item?.iaConfiguration?.attachments ? item?.iaConfiguration?.attachments : undefined}
                  likesEnabled={this.props.messagesStore.likesEnabled && !item?.iaConfiguration?.disableLikes}
                  commentsEnabled={this.props.messagesStore.commentsEnabled && !item?.iaConfiguration?.disableComments}
                  likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
                  imageViewerImagesToShow={() => { }}
                  imageViewerIndexToShow={() => { }}
                  isFullPage={this.props.messagesStore.isFullPageView}
                  padding={"0px"}
                />
              </div>
              {item?.id != undefined && !this.props.messagesStore.loading &&
                <IALikesAndComments
                  itemId={`${item?.id}`}
                  tenant={this.props.messagesStore.tenant}
                  component={this.props.messagesStore.component}
                  instance={this.props.messagesStore.instance}
                  token={this.props.messagesStore.token}
                  environment={this.props.messagesStore.environment}
                  user={this.props.messagesStore.user}
                  imageUploadUrl={`${Helper.getMainAPIUrl(this.props.messagesStore.environment)}/api/${this.props.messagesStore.tenant}.sharepoint.com/image?relativeUrl=Lists/IAImages/Comments&site=IntraActive-Admin`}
                  highlightColor={this.props.messagesStore.highlightColor}
                  color={this.props.messagesStore.textColor}
                  source={"SharePoint"}
                  likesEnabled={this.props.messagesStore.likesEnabled && !item?.iaConfiguration?.disableLikes}
                  commentsEnabled={this.props.messagesStore.commentsEnabled && !item?.iaConfiguration?.disableComments}
                  likesOnCommentsEnabled={this.props.messagesStore.commentsEnabled}
                  newCommentPopupIsShowing={() => { }}
                  likes={item?.likes}
                  showEmojiPickerExpandButton={this.props.messagesStore.instance?.settings?.showEmojiPickerExpandButton}
                  reactionOptions={this.props.messagesStore.instance?.settings?.reactionOptions}
                  commentCount={item?.commentCount}
                  webpartId={this.props.messagesStore.webpartId}
                  isMobile={this.props.messagesStore.isMobile}
                  likesUpdated={(likes: any[]) => {
                    this.props.messagesStore.messages[0].likes = likes;
                  }}
                  commentsUpdated={(comments: number) => {
                    this.props.messagesStore.messages[0].commentCount = comments;
                  }}
                  commentClicked={() => {
                    this.props.messagesStore.updateEditorState();
                  }}
                  commentCanceled={() => {
                    this.props.messagesStore.updateEditorState();
                  }}
                  style={{
                    marginTop: 20,
                    marginBottom: 0,
                    transition: "opacity 200ms"
                  }}
                  padding={20}
                />
              }
            </div>
          )
        }
      </div>
    );
  }
}